import {
  BEAUTIFI_MAXIMUM_AMOUNT,
  BEAUTIFI_MINIMUM_AMOUNT,
} from "atoms/beautifi/NotAvailableBeautifi/NotAvailableBeautifi";

/**
 * Order-size brackets for the illustrative 0% term supplied by Beautifi. Sorted ascending; the
 * first bracket the item/cart total fits under wins, and the last is the catch-all.
 */
const TERM_BRACKETS_IN_MONTHS: { belowInCents: number; termMonths: number }[] = [
  { belowInCents: 250_000, termMonths: 24 },
  { belowInCents: 500_000, termMonths: 48 },
  { belowInCents: Number.POSITIVE_INFINITY, termMonths: 60 },
];

/**
 * Platform-level gate for every Beautifi promo surface. Affirm keeps precedence while both
 * flags are on (mutual exclusivity is enforced operationally, not in code yet), so promos
 * render only when Beautifi is enabled and Affirm is not.
 */
export function isBeautifiPromoEnabled(
  platformSettings: { beautifiEnabled?: boolean; affirmEnabled?: boolean } | null | undefined,
): boolean {
  return platformSettings?.beautifiEnabled === true && platformSettings?.affirmEnabled !== true;
}

/**
 * The financing terms disclosure on detail pages reveals when either financing provider is
 * enabled (R19); on platforms with neither, it stays hidden.
 */
export function isFinancingDisclosureVisible(
  platformSettings: { beautifiEnabled?: boolean; affirmEnabled?: boolean } | null | undefined,
): boolean {
  return platformSettings?.affirmEnabled === true || platformSettings?.beautifiEnabled === true;
}

/**
 * Item-level gate: the price must sit inside the same transaction window checkout enforces
 * (bounds are cents, inclusive).
 */
export function isWithinBeautifiWindow(priceInCents: number): boolean {
  return priceInCents >= BEAUTIFI_MINIMUM_AMOUNT && priceInCents <= BEAUTIFI_MAXIMUM_AMOUNT;
}

/**
 * The term, in months, for the illustrative monthly payment: under $2,500 uses 24 months;
 * $2,500–under $5,000 uses 48 months; and $5,000 or more uses 60 months.
 */
export function getBeautifiIllustrativeTermInMonths(priceInCents: number): number {
  return TERM_BRACKETS_IN_MONTHS.find(({ belowInCents }) => priceInCents < belowInCents)!
    .termMonths;
}

/**
 * The item/cart total divided by its illustrative term, rounded to the nearest cent.
 */
export function getBeautifiMonthlyPaymentInCents(priceInCents: number): number {
  return Math.round(priceInCents / getBeautifiIllustrativeTermInMonths(priceInCents));
}

/**
 * What a Beautifi promo surface should say about a price.
 *
 * Returns a monthly quote inside Beautifi's transaction window and null outside it.
 */
export type BeautifiRateQuote = { monthlyPaymentInCents: number };

export function getBeautifiRateQuote(priceInCents: number): BeautifiRateQuote | null {
  if (!isWithinBeautifiWindow(priceInCents)) {
    return null;
  }

  return {
    monthlyPaymentInCents: getBeautifiMonthlyPaymentInCents(priceInCents),
  };
}
